import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface GymMessageWithSender {
  id: string;
  gym_id: string;
  user_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
  sender: {
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
    dni: string | null;
    username: string | null;
  } | null;
}

export const useGymMessages = (gymId: string | undefined) => {
  return useQuery({
    queryKey: ["gym-messages", gymId],
    enabled: !!gymId,
    queryFn: async (): Promise<GymMessageWithSender[]> => {
      const { data: messages, error } = await supabase
        .from("gym_messages")
        .select("id, gym_id, user_id, content, is_read, created_at")
        .eq("gym_id", gymId!)
        .order("created_at", { ascending: false });

      if (error) throw error;
      if (!messages || messages.length === 0) return [];

      const userIds = Array.from(new Set(messages.map((m) => m.user_id)));
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, avatar_url, dni, username")
        .in("id", userIds);

      const profileMap = new Map((profiles ?? []).map((p) => [p.id, p]));

      return messages.map((m) => ({
        ...m,
        sender: profileMap.get(m.user_id) ?? null,
      }));
    },
  });
};

export const useMarkMessageRead = (gymId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_read }: { id: string; is_read: boolean }) => {
      const { error } = await supabase
        .from("gym_messages")
        .update({ is_read })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gym-messages", gymId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "No se pudo actualizar el mensaje");
    },
  });
};

export const useDeleteGymMessage = (gymId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("gym_messages").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Mensaje eliminado");
      queryClient.invalidateQueries({ queryKey: ["gym-messages", gymId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "No se pudo eliminar el mensaje");
    },
  });
};
